"use client";

import React, { useCallback, useEffect, useState, useRef } from "react";
import { Box, MenuItem, TextField } from "@mui/material";
import Layout4 from "../../DataLayouts/Layout4";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import {
  DESIGNATION_DEFAULT_COLUMNS,
  DESIGNATIONS_DATAGRID_KEY,
  mapSavedDesignationColumns,
  resolveDesignationApiField,
} from "./designationListConstants";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_designation_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({
        field: resolveDesignationApiField(field),
        operator,
        value: filterValue,
      });
    }
  });

  return filters;
};

function OrganizationDesignationsList() {
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DESIGNATION_DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const { userData } = useAuthStore();
  const org = userData?.organization;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const datagridKey = `${DESIGNATIONS_DATAGRID_KEY}_${org.organization_id}`;
        const configRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            datagrid_key: datagridKey,
            organization_id: org.organization_id,
          }),
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const serverCfg =
            configResponse?.datagrid?.datagrid_default_configuration;

          if (serverCfg?.columns?.length > 0) {
            setTableConfig(serverCfg);
            setConfigColumns(mapSavedDesignationColumns(serverCfg.columns));
          } else {
            setTableConfig(null);
            setConfigColumns(DESIGNATION_DEFAULT_COLUMNS);
          }
        } else {
          setTableConfig(null);
          setConfigColumns(DESIGNATION_DEFAULT_COLUMNS);
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setTableConfig(null);
        setConfigColumns(DESIGNATION_DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const departmentId = String(
          searchParams.get("organization_department_id") || ""
        ).trim();
        const requestBody = {
          filters,
          ...(departmentId && departmentId.toLowerCase() !== "all"
            ? { organization_department_id: departmentId }
            : {}),
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: resolveDesignationApiField(
              searchParams.get("sortBy") || "organization_designation_id"
            ),
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/designations-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && Array.isArray(result.data)) {
          setDesignations(result.data);

          if (result.pagination) {
            const pageSize =
              result.pagination.pageSize || result.pagination.limit || 10;
            setPaginationData({
              page:
                result.pagination.page ||
                Number.parseInt(searchParams.get("page"), 10) ||
                1,
              pageSize,
              total: result.pagination.total || 0,
              totalPages: result.pagination.totalPages || 1,
              hasNext: result.pagination.hasNext || false,
              hasPrev: result.pagination.hasPrev || false,
            });
          }
        } else {
          setDesignations([]);
          setPaginationData(DEFAULT_PAGINATION);
          if (result.message || result.error) {
            toast.error(result.message || result.error);
          }
        }
      } catch (err) {
        if (err.name === "AbortError") return;

        console.error("Error fetching designations:", err);
        toast.error("Failed to load designations");
        setDesignations([]);
        setPaginationData(DEFAULT_PAGINATION);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  const deletedesignation = useCallback(
    async (id) => {
      try {
        const org_id = org.organization_id;
        const response = await axios.delete(
          `${MAIN_URL}/api/organizations/${org_id}/designation/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            validateStatus: (status) => status >= 200 && status < 500,
          }
        );

        if (response.status === 200) {
          toast.success(response.data.message);
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const errorMessage =
            response.data.message ||
            response.data.errors?.[Object.keys(response.data.errors)[0]]?.[0] ||
            "Failed to delete Designation";

          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Delete failed:", error);
          toast.error("Something went wrong. Please try again later.");
        }
      }
    },
    [org?.organization_id, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/organization/designation/edit/${item.id || item.organization_designation_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/organization/designation/view/${item.id || item.organization_designation_id}`
      );
    },
    [navigate]
  );

  useEffect(() => {
    if (!org?.organization_id) {
      setDepartmentOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/department-all`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (cancelled) return;
        const rows = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        const options = rows
          .map((item) => ({
            id: item?.organization_department_id ?? item?.id,
            name: String(item?.department_name || "").trim(),
          }))
          .filter((item) => item.id != null && item.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setDepartmentOptions(options);
      } catch (err) {
        console.error("Error fetching departments for designation filter:", err);
        if (!cancelled) setDepartmentOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  const selectedDepartment = (() => {
    const raw = String(searchParams.get("organization_department_id") || "").trim();
    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
  })();
  const handleDepartmentFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value !== "All") next.set("organization_department_id", value);
      else next.delete("organization_department_id");
      next.set("page", "1");
      return next;
    });
  };
  const departmentToolbar = (
    <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
      <TextField
        select
        size="small"
        label="Department"
        value={selectedDepartment}
        onChange={handleDepartmentFilterChange}
        sx={{
          width: 280,
          minWidth: 280,
          "& .MuiSelect-select": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
      >
        <MenuItem value="All">All</MenuItem>
        {departmentOptions.map((department) => (
          <MenuItem key={String(department.id)} value={String(department.id)}>
            {department.name}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", resolveDesignationApiField(field));
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Designations"}
        btnName={"Add Designation"}
        Data={designations}
        add_action={"DESIGNATION_ADD"}
        delete_action={"DESIGNATION_DELETE"}
        Icons={[
          <LocalPoliceIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NextWeekIcon color="primary" />,
          <LocalPoliceIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "department's designation",
          "department designations",
          "Add Department's designations",
          "department designation, Deleting this designation will also remove all related employees",
        ]}
        Route={"/organization/designation"}
        setData={setDesignations}
        DeleteFunc={deletedesignation}
      />

      <div className="ems-section-divider" />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_designations"
          primaryKey="organization_designation_id"
          heading="Designations"
          data={designations}
          sortname="designation_name"
          showActions={true}
          Route="/organization/designation"
          filterModule="organization_designations"
          DeleteFunc={deletedesignation}
          EditFunc={handleEdit}
          edit_delete_action={["DESIGNATION_DELETE", "DESIGNATION_EDIT"]}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          loadedBackendConfig={tableConfig}
          loading={loading || loadingConfig}
          toolbarCenterContent={departmentToolbar}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default OrganizationDesignationsList;
